import React, { useEffect, useRef, useState } from 'react';
import {
  Column,
  Copy,
  Flex,
  Row,
  Square,
  Stack,
} from '@grubhubprod/gh-cookbook-react';
import { useAnalytics } from 'src/components/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from 'src/components/AuthProvider/AuthProvider';
import PageHeader from 'src/components/enterprise/layout/PageHeader';
import { PageTitle } from 'src/components/enterprise/layout/PageTitle/PageTitle';
import { EmployeeListTable } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/EmployeeListTable/EmployeeListTable';
import { RouterButtonLink } from 'src/components/RouterLink/RouterButtonLink';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { buildCMUIPath, CMUIPage } from 'src/constants/urls';
import { AddEmployeesDialog } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/AddEmployeesDialog/AddEmployeesDialog';
import { BulkUploadEmployeesButton } from 'src/components/enterprise/features/Employee/EmployeeListPage/components/BulkUploadEmployeesButton/BulkUploadEmployeesButton';
import { DataTableRef } from 'src/components/DataTable';
import { EmployeeListFilters } from 'src/components/enterprise/features/Employee/utils/employee.utils';
import { useFeatureFlag } from 'src/hooks/useFeatureFlag';
import { FeatureFlag } from 'src/types/FeatureFlag';

export function EmployeeListPage(): JSX.Element {
  const { sendTealiumPageView } = useAnalytics();
  const { corpId } = useAuth();
  const { isDesktop } = useScreenSizes();

  const { value: isNewEmployeesBulkUploadPageEnabled } = useFeatureFlag(
    FeatureFlag.ENABLE_NEW_EMPLOYEES_BULK_UPLOAD_PAGE
  );

  const [isBulkUploadDialogOpen, setIsBulkUploadDialogOpen] = useState(false);

  const tableRef = useRef<DataTableRef<EmployeeListFilters>>(null);

  const handleCloseBulkUploadDialog = (): void => {
    setIsBulkUploadDialogOpen(false);
  };

  const handleFileUploadFinished = (): void => {
    tableRef.current?.refresh();
  };

  useEffect(() => {
    sendTealiumPageView({
      pagegroup: 'corporate admin',
      pagesubgroup: 'manage employees_home',
    });
    // eslint-disable-next-line no-autofix/react-hooks/exhaustive-deps
  }, []);

  return (
    <Square inset="lg">
      <PageHeader>
        <Row alignItems="center">
          <Column desktop={7} tablet={12} phone={12}>
            <Stack y="x-sm">
              <PageTitle title="Employees" />
            </Stack>
            <Stack y={isDesktop ? 'none' : 'md'}>
              <Copy variant="body">
                View and manage employees from your organization who are
                eligible to order food through their employer benefit.
              </Copy>
            </Stack>
          </Column>
          <Column desktop={5} tablet={12} phone={12}>
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent={isDesktop ? 'flex-end' : 'flex-start'}
            >
              <Stack x="x-sm">
                <BulkUploadEmployeesButton
                  corpId={corpId}
                  setIsBulkUploadDialogOpen={setIsBulkUploadDialogOpen}
                />
              </Stack>
              <Stack x="x-sm">
                <RouterButtonLink
                  href={buildCMUIPath(CMUIPage.EMPLOYEE_CREATE, { corpId })}
                  buttonVariant="primary"
                  buttonSize="medium"
                >
                  Add New Employee
                </RouterButtonLink>
              </Stack>
            </Flex>
          </Column>
        </Row>
      </PageHeader>
      <EmployeeListTable ref={tableRef} />
      {isNewEmployeesBulkUploadPageEnabled && (
        <AddEmployeesDialog
          isOpen={isBulkUploadDialogOpen}
          onConfirm={handleCloseBulkUploadDialog}
          onDismiss={handleCloseBulkUploadDialog}
          onFileUploadFinished={handleFileUploadFinished}
        />
      )}
    </Square>
  );
}
